const BACKEND_URI = "";

import { ChatAppResponse, ChatAppResponseOrError, ChatAppRequest, Config, SimpleAPIResponse, HistoryListApiResponse, HistoryApiResponse, SubmitFeedbackResponse, SubmitFeedbackRequest, FeedbackAnalyzeRequest, FeedbackAnalyzeResponse, AgentsApiResponse } from "./models";
import { useLogin, getToken } from "../authConfig";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    if (useLogin && idToken) {
        return { Authorization: `Bearer ${idToken}` };
    }

    return {};
}

export async function configApi(idToken?: string): Promise<Config> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET",
        headers: { ...headers }
    });

    return (await response.json()) as Config;
}

export async function getAgentsApi(idToken: string | undefined): Promise<AgentsApiResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/agents`, {
        method: "GET",
        headers: { ...headers }
    });

    if (!response.ok) {
        throw new Error(`Getting agents failed: ${response.statusText}`);
    }

    return (await response.json()) as AgentsApiResponse;
}

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (response.status > 299 || !response.ok) {
        throw Error(`Request failed with status ${response.status}`);
    }
    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (parsedResponse.error) {
        throw Error(parsedResponse.error);
    }

    return parsedResponse as ChatAppResponse;
}

export async function chatApi(request: ChatAppRequest, shouldStream: boolean, idToken: string | undefined): Promise<Response> {
    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream/v2";  // Updated to use LangChain v1.0 endpoint
    }
    const headers = await getHeaders(idToken);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                return null;
            } else {
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

export async function uploadFileApi(request: FormData, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/upload", {
        method: "POST",
        headers: await getHeaders(idToken),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(filename: string, idToken: string): Promise<SimpleAPIResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(idToken: string): Promise<string[]> {
    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: await getHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function postChatHistoryApi(item: any, idToken: string, agentType?: string): Promise<any> {
    // If auth is enabled but we don't have a token yet, skip persisting history for this call.
    // This prevents unhandled 401s from interrupting the chat UX.
    if (useLogin && !idToken) {
        return {};
    }

    const headers = await getHeaders(idToken);
    const body = agentType ? { ...item, agent_type: agentType } : item;
    const response = await fetch("/chat_history", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 401) {
            return {};
        }
        throw new Error(`Posting chat history failed: ${response.statusText}`);
    }

    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getChatHistoryListApi(count: number, continuationToken: string | undefined, idToken: string, agentType?: string): Promise<HistoryListApiResponse> {
    const headers = await getHeaders(idToken);
    let url = `${BACKEND_URI}/chat_history/sessions?count=${count}`;
    if (continuationToken) {
        url += `&continuation_token=${encodeURIComponent(continuationToken)}`;
    }
    if (agentType) {
        url += `&agent_type=${encodeURIComponent(agentType)}`;
    }

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat histories failed: ${response.statusText}`);
    }

    const dataResponse: HistoryListApiResponse = await response.json();
    return dataResponse;
}

export async function getChatHistoryApi(id: string, idToken: string): Promise<HistoryApiResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/chat_history/sessions/${id}`, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }

    const dataResponse: HistoryApiResponse = await response.json();
    return dataResponse;
}

export async function deleteChatHistoryApi(id: string, idToken: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/chat_history/sessions/${id}`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
}

export async function submitFeedbackApi(request: SubmitFeedbackRequest, idToken: string): Promise<SubmitFeedbackResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/feedback`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409 || errorData.code === "DUPLICATE_FEEDBACK") {
            throw new Error("already submitted");
        }
        throw new Error(`Submitting feedback failed: ${response.statusText}`);
    }

    return await response.json();
}

export async function analyzeFeedbackApi(request: FeedbackAnalyzeRequest, idToken: string): Promise<FeedbackAnalyzeResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/feedback/analyze`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Analyzing feedback failed: ${response.statusText}`);
    }

    return await response.json();
}

export function getSharePointUrl(citation: string, searchResults: any[]): string | null {
    const matchingResult = searchResults.find(result => 
        result.sourcepage === citation || 
        result.sourcefile === citation.split('#')[0]
    );
    
    return matchingResult?.sharepointUrl || null;
}

export function getOriginalFilename(citation: string, searchResults: any[]): string | null {
    const matchingResult = searchResults.find(result => 
        result.sourcepage === citation || 
        result.sourcefile === citation.split('#')[0]
    );
    
    return matchingResult?.originalFilename || null;
}
